import React from "react";
import "./governmentalEntity.scss";
import { BasicIcon, Tooltip, ToolTipPosition, Tag, HeaderDescription, FileInput, Button } from "@pickit/pickit-components";
import { useTranslation } from "react-i18next";
import { Form, Field } from "react-final-form";
import "./i18n";
import { formatDDMMYYYY } from "utils/date";
import { GovernmentalEntityProps } from "./types";
import { VersionHistoryCard } from "component/documentManager/VersionHistoryCard";

const MAX_FILE_SIZE_MB = 4;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export const GovernmentalEntity: React.FC<GovernmentalEntityProps> = ({
  testId = "governmental-entity",
  currentFile,
  fileStatus,
  versions,
  loading,
  fileHandler,
  fileInputKey,
  downloadVersion,
  onUpdate,
}): JSX.Element => {
  const { t } = useTranslation();

  const validateCsvFile = (value: unknown) => {
    const file = value as File | undefined;
    if (!file || !(file instanceof File)) return undefined;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      return t("governmentalEntity:invalidFileType");
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return t("governmentalEntity:fileTooLarge", { max: MAX_FILE_SIZE_MB });
    }
    return undefined;
  };

  const tableHeaders = [
    {
      label: t("governmentalEntity:version"),
      key: "version",
      col: 3,
      testId: "version",
    },
    { label: t("governmentalEntity:estado"), key: "estado", col: 4, testId: "state" },
    { label: t("governmentalEntity:uploadDate"), key: "uploadDate", col: 5, testId: "uploadDate" },
    { label: t("governmentalEntity:uploadedBy"), key: "uploadedBy", testId: "uploadedBy" },
    { label: t("governmentalEntity:download"), key: "actions", col: 3, testId: "actions" },
  ];

  const tableData =
    versions && Array.isArray(versions)
      ? versions.map((version, index) => ({
          version: "V" + (version.version ?? "—"),
          estado:
            index === 0 ? (
              <Tag status="success" label={t("governmentalEntity:activa")} />
            ) : null,
          uploadDate: version.uploadedAt
            ? formatDDMMYYYY(version.uploadedAt)
            : "—",
          uploadedBy: version.uploadedBy?.email || "—",
          actions: (
            <div className="actions-container">
                <div />
                <div />
                <div data-testId={`${testId}-download-version-${version.id}`}>
                  <BasicIcon
                    clickable={true}
                    onClick={() =>
                      downloadVersion(
                        version.id.toString(),
                        version.url || "",
                        version.fileName
                      )
                    }
                    name="download"
                    size="small"
                  />
                </div>
            </div>
          ),
        }))
      : [];

  return (
    <div className="governmental-entity-page">
      <HeaderDescription
        headline={t("governmentalEntity:title")}
        overline="Administración"
        testId={`${testId}-title`}
        description={t("governmentalEntity:subtitle")}
      />
      <div className="governmental-entity-content">
        <div className="governmental-entity-two-columns">
        <div className="doc-manager-card-header">
          <h2 className="doc-manager-section-title" data-testId={`${testId}-update-file-title`}>
            {t("governmentalEntity:updateFile")}
            <Tooltip message={t("governmentalEntity:tooltipInfo")} position={ToolTipPosition.bottomRight}>
              <BasicIcon
                name="information-circle"
                size="small"
                color="#0098ff"
                data-testId={`${testId}-tooltip-info`}
              />
            </Tooltip>
          </h2>
          <Form key={fileInputKey} onSubmit={onUpdate} subscription={{ invalid: true }}>
            {({ handleSubmit, invalid }) => (
              <form onSubmit={handleSubmit}>
                <div className="file-input-container">
                  <Field name="file" validate={validateCsvFile}>
                    {({ input, meta }) => (
                      <FileInput
                        testId={testId}
                        label={t("governmentalEntity:fileLabel")}
                        input={{
                          ...input,
                          onChange: (value: unknown) => {
                            input.onChange(value);
                            input.onBlur();
                            const file = value as File;
                            if (file instanceof File) {
                              fileHandler(file);
                            }
                          },
                        }}
                        meta={meta}
                        accept=".csv"
                        fileTypes={[".csv"]}
                        dragable
                        truncateFileName
                        helperText={
                          !currentFile?.url
                            ? t("governmentalEntity:fileHelperText")
                            : t("governmentalEntity:fileReplaceText")
                        }
                      />
                    )}
                  </Field>
                </div>
                <div className="doc-manager-button-container">
                  <Button
                    type="submit"
                    testId={testId}
                    label={t("governmentalEntity:loadFile")}
                    hierarchy="primary"
                    size="large"
                    width="220"
                    disabled={!currentFile?.url || fileStatus.loading || invalid}
                  />
                </div>
              </form>
            )}
          </Form>
        </div>
          <VersionHistoryCard
            testId={testId}
            title={t("governmentalEntity:versionHistory")}
            headers={tableHeaders}
            data={tableData}
            loading={loading}
            loadingText={
              t("governmentalEntity:loadingVersions") || "Cargando versiones..."
            }
            emptyText={t("governmentalEntity:noVersions")}
          />
        </div>
      </div>
    </div>
  );
};

export default GovernmentalEntity;
